/**
 * Pure display / layout / annotation-color helpers extracted from EEGViewer
 * to reduce initial parse cost of the main component module.
 */

import { LIVE_TOPOGRAPHY_ALIASES } from "./eegViewerLiveTopography";
import type { ChannelMapAliasMaps } from "../utils/channelMaps/lifelinesT4aDefault";

export interface AnnotationTypeCatalogEntry {
  annotation_type_id: string;
  type_code: string;
  display_name: string;
  category: string;
  color: string;
  icon?: string | null;
  description?: string | null;
  default_duration_seconds?: number | null;
  min_duration_seconds?: number | null;
  max_duration_seconds?: number | null;
  supports_duration?: boolean;
  supports_notes?: boolean;
  supports_channel_scope?: boolean;
  supports_drag_move?: boolean;
  supports_resize?: boolean;
  quick_action_enabled?: boolean;
  quick_action_order?: number | null;
  prune_capability?: "none" | "virtual" | "physical" | "both" | string;
}


const TECHNICALLY_IMPORTED_ANNOTATION_COLOR = "#64748b";

function normalizeAnnotationTypeCode(value?: string | null): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizeHexColor(color: unknown): string | null {
  if (typeof color !== "string") return null;
  const value = color.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(value)) return null;
  return value.toLowerCase();
}

export function resolveEEGViewerAnnotationDisplayColor(params: {
  annotationType?: string | null;
  annotationColor?: string | null;
  catalogColor?: string | null;
  fallbackColor?: string | null;
  isTechnicalImported?: boolean;
}): string {
  const typeCode = normalizeAnnotationTypeCode(params.annotationType);
  const explicitColor = normalizeHexColor(params.annotationColor);
  const catalogColor = normalizeHexColor(params.catalogColor);
  const isLegacyGenericArtifactYellow =
    typeCode === "artifact" && explicitColor === "#ffff00";

  if (explicitColor && !isLegacyGenericArtifactYellow) return explicitColor;
  if (catalogColor) return catalogColor;
  if (explicitColor) return explicitColor;

  const fallbackColor = normalizeHexColor(params.fallbackColor);
  if (fallbackColor) return fallbackColor;

  return params.isTechnicalImported
    ? TECHNICALLY_IMPORTED_ANNOTATION_COLOR
    : "#f59e0b";
}


const LEGACY_PLACEHOLDER_MONTAGE_IDS = new Set([
  "standard-10-20",
  "standard_10_20",
  "standard-1020",
]);

const REFERENTIAL_CHANNEL_SUFFIXES = [
  "-REF",
  "-LE",
  "-RE",
  "-AVG",
  "-AV",
  "-AR",
  "-AAR",
  "-CAR",
] as const;

function stripModalityPrefix(name: string): string {
  return name.trim().toUpperCase().replace(/^(EEG|ECG|EMG|EOG)\s+/, "");
}

export function recordingElectrodeToken(name: string): string {
  let token = stripModalityPrefix(name);
  const suffix = [...REFERENTIAL_CHANNEL_SUFFIXES]
    .sort((left, right) => right.length - left.length)
    .find((candidate) => token.endsWith(candidate));
  if (suffix) {
    token = token.slice(0, -suffix.length);
  }
  token = token.replace(/[^A-Z0-9]+/g, "");
  return LIVE_TOPOGRAPHY_ALIASES[token] ?? token;
}

function normalizeAcquisitionLookupKey(name: string): string {
  const bare = stripModalityPrefix(name);
  const chMatch = bare.match(/^CH(\d+)(?:-REF)?$/i);
  if (chMatch) {
    return `CH${Number(chMatch[1])}`;
  }
  return bare.replace(/[^A-Z0-9]+/g, "");
}

function findAvailableChannelByAcquisitionName(
  acquisitionName: string,
  availableChannelNames: string[],
): string | null {
  const targetKey = normalizeAcquisitionLookupKey(acquisitionName);
  const targetUpper = acquisitionName.trim().toUpperCase();
  const targetBare = stripModalityPrefix(acquisitionName);

  const exact = availableChannelNames.find((channelName) => {
    const upper = channelName.trim().toUpperCase();
    return (
      upper === targetUpper ||
      stripModalityPrefix(channelName) === targetBare ||
      upper === `EEG ${targetBare}`
    );
  });
  if (exact) return exact;

  return (
    availableChannelNames.find(
      (channelName) =>
        normalizeAcquisitionLookupKey(channelName) === targetKey,
    ) || null
  );
}

export function resolveRecordingChannelName(
  requestedName: string,
  availableChannelNames: string[] | null | undefined,
  aliasMaps?: ChannelMapAliasMaps | null,
): string | null {
  if (!requestedName.trim() || !availableChannelNames?.length) {
    return null;
  }

  const requestedBare = stripModalityPrefix(requestedName);
  const exactCandidates = new Set([
    requestedName.trim().toUpperCase(),
    requestedBare,
    `EEG ${requestedBare}`,
  ]);
  const exactMatch = availableChannelNames.find((channelName) =>
    exactCandidates.has(channelName.trim().toUpperCase()),
  );
  if (exactMatch) return exactMatch;

  const requestedToken = recordingElectrodeToken(requestedName);
  if (!requestedToken) return null;

  const tokenMatch = availableChannelNames.find(
    (channelName) => recordingElectrodeToken(channelName) === requestedToken,
  );
  if (tokenMatch) return tokenMatch;

  if (!aliasMaps) return null;

  const acquisitionFromClinical =
    aliasMaps.clinicalToAcquisition.get(requestedToken) ||
    aliasMaps.clinicalToAcquisition.get(
      requestedBare.replace(/[^A-Z0-9]+/g, ""),
    );
  if (acquisitionFromClinical) {
    const resolved = findAvailableChannelByAcquisitionName(
      acquisitionFromClinical,
      availableChannelNames,
    );
    if (resolved) return resolved;
  }

  const viaForward = availableChannelNames.find((channelName) => {
    const acqKey = normalizeAcquisitionLookupKey(channelName);
    const clinical = aliasMaps.acquisitionToClinical.get(acqKey);
    if (!clinical) return false;
    return recordingElectrodeToken(clinical) === requestedToken;
  });
  return viaForward || null;
}

type EffectiveViewerMontage = {
  montage_id?: string;
  montage_name?: string;
  montage_type?: string;
  is_variant?: boolean;
  variant_id?: string;
  excluded_channels?: string[];
  channel_pairs?: Array<{
    positive: string;
    negative?: string;
    label?: string;
    color?: string;
  }>;
};

export function buildPassthroughMontagePairs(
  recordingChannels: string[] | null | undefined,
): Array<{ positive: string; rendererPositiveName: string }> {
  if (!Array.isArray(recordingChannels) || recordingChannels.length === 0) {
    return [];
  }
  return recordingChannels.map((channel) => ({
    positive: channel.replace(/^EEG\s+/i, ""),
    rendererPositiveName: channel,
  }));
}

export function montagePairsMatchRecordingChannels(
  pairs:
    | Array<{ positive?: string; negative?: string }>
    | null
    | undefined,
  recordingChannels: string[] | null | undefined,
  aliasMaps?: ChannelMapAliasMaps | null,
): boolean {
  if (!pairs?.length) return true;
  if (!recordingChannels?.length) return true;

  const recordingHasElectrode = (token: string | undefined): boolean => {
    const needle = recordingElectrodeToken(token || "");
    if (!needle) return true;
    if (
      recordingChannels.some(
        (channel) => recordingElectrodeToken(channel) === needle,
      )
    ) {
      return true;
    }
    if (!aliasMaps || !token?.trim()) return false;
    return Boolean(
      resolveRecordingChannelName(token, recordingChannels, aliasMaps),
    );
  };

  return pairs.some(
    (pair) =>
      recordingHasElectrode(pair.positive) &&
      recordingHasElectrode(pair.negative),
  );
}

function findPassthroughViewerMontage(
  list: EffectiveViewerMontage[],
): EffectiveViewerMontage | null {
  return (
    list.find((montage) => montage.montage_type === "passthrough") ||
    list.find((montage) =>
      montage.montage_name?.toLowerCase().includes("no derivation"),
    ) ||
    list.find((montage) => !montage.channel_pairs?.length) ||
    null
  );
}

export function resolveEffectiveViewerMontage(
  montages: EffectiveViewerMontage[] | null | undefined,
  montageId?: string | null,
  recordingChannels?: string[] | null,
  aliasMaps?: ChannelMapAliasMaps | null,
): EffectiveViewerMontage | null {
  const list = Array.isArray(montages) ? montages : [];
  if (!list.length) return null;

  // Explicit No Derivation from the viewer select (empty string). Do not
  // promote to Longitudinal / Double Banana when the clinician chose raw.
  if (montageId === "") {
    return (
      findPassthroughViewerMontage(list) || {
        montage_name: "No Derivation",
        montage_type: "passthrough",
        channel_pairs: [],
      }
    );
  }

  const normalizedId = String(montageId || "").trim();
  const requested =
    normalizedId &&
    !LEGACY_PLACEHOLDER_MONTAGE_IDS.has(normalizedId) &&
    list.some((montage) => montage.montage_id === normalizedId)
      ? list.find((montage) => montage.montage_id === normalizedId) || null
      : null;

  const preferred =
    requested ||
    list.find(
      (montage) =>
        montage.montage_name?.toLowerCase().includes("double banana") ||
        montage.montage_name?.toLowerCase().includes("longitudinal bipolar"),
    ) ||
    list.find(
      (montage) =>
        !montage.is_variant && montage.montage_type !== "passthrough",
    ) ||
    list[0] ||
    null;

  if (
    preferred &&
    !montagePairsMatchRecordingChannels(
      preferred.channel_pairs,
      recordingChannels,
      aliasMaps,
    )
  ) {
    return findPassthroughViewerMontage(list) || preferred;
  }

  return preferred;
}

/** True when the effective montage is raw / passthrough (No Derivation). */
export function isPassthroughViewerMontage(
  montage: EffectiveViewerMontage | null | undefined,
): boolean {
  if (!montage) return true;
  if (montage.montage_type === "passthrough") return true;
  if (!montage.channel_pairs?.length) return true;
  return /no derivation/i.test(montage.montage_name || "");
}


export type WaveformDisplayMode = "stacked" | "butterfly";
export type RowHeightMode = "compact" | "standard" | "tall";


type AutoFitWindowData = {
  channels: string[];
  columnArrays: ReadonlyArray<ArrayLike<number> | null | undefined>;
  numSamples: number;
  sampling_rate?: number | null;
};

type AutoFitMontageChannelDetail = {
  label: string;
  positive: string;
  negative: string;
};


const VIEWER_SENSITIVITY_STEPS_FOR_AUTOFIT = [
  2, 3, 5, 7, 10, 15, 20, 30, 50, 70, 100,
];
const DEFAULT_SENSITIVITY_FOR_AUTOFIT = 7;
const DEFAULT_TIME_SCALE_FOR_AUTOFIT = 10;

export const ROW_HEIGHT_CHANNEL_SPACING: Record<RowHeightMode, number> = {
  compact: 0.8,
  standard: 1,
  tall: 1.4,
};

/** Legacy Comfortable/Compact values that missed the 0.1 slider step. */
const LEGACY_CHANNEL_SPACING: Record<string, number> = {
  "0.85": ROW_HEIGHT_CHANNEL_SPACING.compact,
  "1.35": ROW_HEIGHT_CHANNEL_SPACING.tall,
};

export function migrateLegacyChannelSpacing(value: number): number {
  if (!Number.isFinite(value)) return value;
  const mapped = LEGACY_CHANNEL_SPACING[String(value)];
  return typeof mapped === "number" ? mapped : value;
}

export function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function normalizeSensitivityValue(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SENSITIVITY_FOR_AUTOFIT;
  if (value <= 1) return 2;
  if (VIEWER_SENSITIVITY_STEPS_FOR_AUTOFIT.includes(value)) return value;
  return VIEWER_SENSITIVITY_STEPS_FOR_AUTOFIT.reduce((closest, candidate) =>
    Math.abs(candidate - value) < Math.abs(closest - value)
      ? candidate
      : closest,
  );
}

export function getChannelSpacingForRowHeightMode(
  rowHeightMode: RowHeightMode,
): number {
  return (
    ROW_HEIGHT_CHANNEL_SPACING[rowHeightMode] ??
    ROW_HEIGHT_CHANNEL_SPACING.standard
  );
}

export function getRowHeightModeFromChannelSpacing(
  channelSpacing: number,
): RowHeightMode {
  const safeSpacing = Number.isFinite(channelSpacing)
    ? channelSpacing
    : ROW_HEIGHT_CHANNEL_SPACING.standard;

  return (
    Object.entries(ROW_HEIGHT_CHANNEL_SPACING) as Array<[RowHeightMode, number]>
  ).reduce<RowHeightMode>((closestMode, [candidateMode, candidateSpacing]) => {
    const closestDistance = Math.abs(
      ROW_HEIGHT_CHANNEL_SPACING[closestMode] - safeSpacing,
    );
    const candidateDistance = Math.abs(candidateSpacing - safeSpacing);
    return candidateDistance < closestDistance ? candidateMode : closestMode;
  }, "standard");
}

export function getVisibleChannelRefitKey(
  montageChannelDetails: AutoFitMontageChannelDetail[],
): string {
  return montageChannelDetails
    .map((channel) => channel.label.trim())
    .filter(Boolean)
    .join("|");
}

export function shouldTriggerRefitOnVisibleChannelChange({
  enabled,
  displayMode,
  rendererReady,
  hasCurrentConfig,
  hasVisibleWaveformData,
  previousKey,
  nextKey,
}: {
  enabled: boolean;
  displayMode: WaveformDisplayMode;
  rendererReady: boolean;
  hasCurrentConfig: boolean;
  hasVisibleWaveformData: boolean;
  previousKey: string | null;
  nextKey: string;
}): boolean {
  if (!enabled || displayMode !== "stacked") return false;
  if (!rendererReady || !hasCurrentConfig || !hasVisibleWaveformData) {
    return false;
  }
  if (!nextKey || previousKey === null || previousKey === nextKey) {
    return false;
  }
  return true;
}


function getSortedQuantile(sortedValues: number[], quantile: number): number {
  if (sortedValues.length === 0) return Number.NaN;
  if (sortedValues.length === 1) return sortedValues[0];

  const safeQuantile = clampNumber(
    Number.isFinite(quantile) ? quantile : 0.95,
    0,
    1,
  );
  const position = (sortedValues.length - 1) * safeQuantile;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  const lowerValue = sortedValues[lowerIndex];
  const upperValue = sortedValues[upperIndex] ?? lowerValue;

  if (lowerIndex === upperIndex) return lowerValue;
  return lowerValue + (upperValue - lowerValue) * (position - lowerIndex);
}


export function calculateAutoFitSensitivityForWindow({
  eegData,
  montageChannelDetails,
  currentTimeSeconds,
  dataWindowStartSeconds,
  timeScaleSeconds,
  laneHeightPx,
  pixelsPerMm,
  percentile = 0.95,
  targetEnvelopeFillRatio = 0.75,
}: {
  eegData: AutoFitWindowData | null | undefined;
  montageChannelDetails: AutoFitMontageChannelDetail[];
  currentTimeSeconds: number;
  dataWindowStartSeconds: number;
  timeScaleSeconds: number;
  laneHeightPx: number;
  pixelsPerMm: number;
  percentile?: number;
  targetEnvelopeFillRatio?: number;
}): number | null {
  if (!eegData) return null;
  if (
    !Array.isArray(montageChannelDetails) ||
    montageChannelDetails.length === 0
  )
    return null;

  const samplingRate =
    typeof eegData.sampling_rate === "number" &&
    Number.isFinite(eegData.sampling_rate) &&
    eegData.sampling_rate > 0
      ? eegData.sampling_rate
      : 256;
  const totalSamples =
    typeof eegData.numSamples === "number" &&
    Number.isFinite(eegData.numSamples)
      ? Math.max(0, Math.floor(eegData.numSamples))
      : 0;
  if (totalSamples <= 1 || eegData.channels.length === 0) return null;

  const safeTimeScale =
    Number.isFinite(timeScaleSeconds) && timeScaleSeconds > 0
      ? timeScaleSeconds
      : DEFAULT_TIME_SCALE_FOR_AUTOFIT;
  const startSample = clampNumber(
    Math.floor(
      Math.max(0, currentTimeSeconds - dataWindowStartSeconds) * samplingRate,
    ),
    0,
    Math.max(0, totalSamples - 1),
  );
  const leadInSkipSamples =
    startSample === 0
      ? Math.min(
          Math.floor(samplingRate * 0.5),
          Math.max(0, Math.floor((totalSamples - startSample) * 0.1)),
        )
      : 0;
  const analysisStartSample = Math.min(
    totalSamples - 1,
    startSample + leadInSkipSamples,
  );
  const windowSampleCount = clampNumber(
    Math.floor(safeTimeScale * samplingRate),
    Math.max(1, Math.floor(samplingRate * 2)),
    Math.max(1, totalSamples - analysisStartSample),
  );
  const endSampleExclusive = Math.min(
    totalSamples,
    analysisStartSample + windowSampleCount,
  );
  const sampleStride = Math.max(
    1,
    Math.floor((endSampleExclusive - analysisStartSample) / 4000),
  );

  const channelIndexByName = new Map(
    eegData.channels.map((name, index) => [name, index]),
  );
  const absoluteAmplitudesUv: number[] = [];

  montageChannelDetails.forEach((detail) => {
    const positiveIndex = channelIndexByName.get(detail.positive);
    if (typeof positiveIndex !== "number") return;

    const negativeIndex = detail.negative
      ? (channelIndexByName.get(detail.negative) ?? -1)
      : -1;
    const positiveColumn = eegData.columnArrays[positiveIndex];
    const negativeColumn =
      negativeIndex >= 0 ? eegData.columnArrays[negativeIndex] : null;
    if (!positiveColumn) return;

    const windowValues: number[] = [];
    for (
      let sampleIndex = analysisStartSample;
      sampleIndex < endSampleExclusive;
      sampleIndex += sampleStride
    ) {
      const positiveUv = (positiveColumn[sampleIndex] ?? 0) * 1000000;
      const negativeUv = negativeColumn
        ? (negativeColumn[sampleIndex] ?? 0) * 1000000
        : 0;
      const amplitudeUv = positiveUv - negativeUv;
      if (!Number.isFinite(amplitudeUv)) continue;
      windowValues.push(amplitudeUv);
    }
    if (windowValues.length === 0) return;
    const meanUv =
      windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length;
    windowValues.forEach((value) => {
      absoluteAmplitudesUv.push(Math.abs(value - meanUv));
    });
  });

  if (absoluteAmplitudesUv.length === 0) return null;

  absoluteAmplitudesUv.sort((a, b) => a - b);
  const targetAmplitudeUv = getSortedQuantile(absoluteAmplitudesUv, percentile);
  if (!Number.isFinite(targetAmplitudeUv) || targetAmplitudeUv <= 0) {
    return null;
  }

  const safeLaneHeightPx = Math.max(8, laneHeightPx);
  const safePixelsPerMm = clampNumber(
    Number.isFinite(pixelsPerMm) ? pixelsPerMm : 96 / 25.4,
    1,
    40,
  );
  const safeFillRatio = clampNumber(targetEnvelopeFillRatio, 0.55, 0.9);
  const idealSensitivity =
    (2 * targetAmplitudeUv * safePixelsPerMm) /
    (safeLaneHeightPx * safeFillRatio);

  return normalizeSensitivityValue(idealSensitivity);
}


export function isEEGViewerTypingTarget(element: HTMLElement | null): boolean {
  if (!element) return false;
  return (
    ["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName) ||
    element.isContentEditable ||
    element.closest("[contenteditable='true']") !== null ||
    element.getAttribute("role") === "textbox"
  );
}

export function isEEGViewerShortcutFocusWithinViewer(
  viewerRoot: HTMLElement | null,
  activeElement: HTMLElement | null,
  target: EventTarget | null,
): boolean {
  if (!viewerRoot) return false;
  const targetElement = target as HTMLElement | null;
  return Boolean(
    (activeElement && viewerRoot.contains(activeElement)) ||
    (targetElement && viewerRoot.contains(targetElement)),
  );
}

